<script setup lang="ts">
import ChoiceField from '../form/ChoiceField.vue'
import { TextField, CheckboxField } from '@haskoning/vue-component-library'
import { computed } from 'vue'
import { BOULANGER_IDRISS_PROB, MOSS_PROB } from '@/stores/type/calculationCPTDataPoint.type'

const props = defineProps({
  fields: { type: Object, required: true },
  item: { type: Object, required: true },
  readonly: { type: Boolean, required: false, default: false },
  disabled: { type: Boolean, required: false, default: false }
})

const modelValue = computed({
  get: () => props.item,
  set: async (value) => {
    if (props.readonly || props.disabled) {
      return
    }
    emit('update', value)
    emit('update:item', value)
  }
})

const updateItem = async (): Promise<void> => {
  if (props.readonly) {
    return
  }
  emit('update', modelValue.value)
}

const showCRR = computed(() => {
  return (
    props.item.liquefaction_method.includes(BOULANGER_IDRISS_PROB) ||
    props.item.liquefaction_method.includes(MOSS_PROB)
  )
})

const custom_liquefaction_method_field = computed(() => {
  const choice = props.fields['liquefaction_method'].child?.choices?.filter(
    (choice: { value: string, display_name: string }) => choice.value !== MOSS_PROB
  )
  return {
    ...props.fields['liquefaction_method'],
    child: {
      ...props.fields['liquefaction_method'].child,
      choices: choice
    }
  }
})

const emit = defineEmits(['update:item', 'update'])
</script>

<template>
  <v-list-item>
    <v-list-item-title> Seismicity input</v-list-item-title>
  </v-list-item>
  <v-list-item>
    <v-row no-gutters>
      <v-col cols="6" class="pr-2">
        <TextField
          :field="fields['earthquake_magnitude']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
          warning
          required
          :warning-rules="[
            {
              min_value: 5.5,
              message: `Earthquake magnitude (Mw) falls out of range (5.5 < Mw < 8.5), this may lead to inaccurate results`
            },
            {
              max_value: 8.5,
              message: `Earthquake magnitude (Mw) falls out of range (5.5 < Mw < 8.5), this may lead to inaccurate results`
            }
          ]"
        ></TextField>
      </v-col>
      <v-col cols="6">
        <TextField
          :field="fields['max_acceleration']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
          required
        ></TextField>
      </v-col>
    </v-row>
  </v-list-item>
  <v-list-item>
    <v-list-item-title>Calculation options</v-list-item-title>
  </v-list-item>
  <v-list-item>
    <v-row no-gutters>
      <v-col cols="6" class="pr-2">
        <ChoiceField
          :field="fields['soil_classification']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>
      <v-col cols="6">
        <ChoiceField
          :field="fields['soil_unit_weight']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="true"
        ></ChoiceField>
      </v-col>
      <v-col class="pr-2" cols="6">
        <ChoiceField
          :field="custom_liquefaction_method_field"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>
      <v-col cols="6">
        <ChoiceField
          v-if="showCRR"
          :field="fields['CRR_limit']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>

      <v-col class="pr-2" cols="6">
        <ChoiceField
          :field="fields['relative_density']"
          v-model="modelValue"
          hide-details
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>
      <v-col cols="6">
        <ChoiceField
          :field="fields['liquefaction_settlement']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>

      <v-col cols="6" class="pr-2">
        <ChoiceField
          :field="fields['Ic_cutoff']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        ></ChoiceField>
      </v-col>

      <v-col cols="6">
        <TextField
          v-if="item['Ic_cutoff']"
          :field="fields['Ic_limit']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
          warning
          :warning-rules="[
            {
              min_value: 2.6,
              message: `Please be warned when changing the default Ic limit (2.6)`
            },
            {
              max_value: 2.6,
              message: `Please be warned when changing the default Ic limit (2.6)`
            }
          ]"
        ></TextField>
      </v-col>
    </v-row>
  </v-list-item>
  <v-list-item class="pt-4">
    <v-list-item-title>Ground water level [elevation]</v-list-item-title>
  </v-list-item>

  <v-list-item>
    <v-row no-gutters>
      <v-col cols="6" class="pr-2">
        <TextField
          :field="fields['ground_water_table_insitu']"
          v-model="modelValue"
          required
          :readonly="readonly"
          :disabled="disabled"
        ></TextField>
      </v-col>
      <v-col cols="6">
        <TextField
          :field="fields['ground_water_table_design']"
          :step="0.1"
          v-model="modelValue"
          required
          :readonly="readonly"
          :disabled="disabled"
        ></TextField>
      </v-col>
    </v-row>
  </v-list-item>

  <v-list-item>
    <v-list-item-title>
      <div
        class="d-inline pr-2"
        style="border-right-style: solid; border-right-width: 1px; border-right-color: #dadada"
      >
        Site conditions
      </div>
      <v-divider class="pl-2" :vertical="true"></v-divider>
      <v-btn-toggle
        density="compact"
        v-model="modelValue.has_site_design_condition"
        @update:model-value="updateItem"
        color="primary"
        group
      >
        <v-btn
          class="pl-4 text-none"
          :variant="!item.has_site_design_condition ? 'elevated' : 'tonal'"
          slim
          density="compact"
          :value="false"
          :append-icon="!item.has_site_design_condition ? 'mdi-check' : 'empty'"
          :readonly="readonly"
          :disabled="disabled"
        >
          Native condition
        </v-btn>
        <v-btn
          slim
          class="pl-4 text-none"
          :variant="item.has_site_design_condition ? 'elevated' : 'tonal'"
          density="compact"
          :value="true"
          :append-icon="item.has_site_design_condition ? 'mdi-check' : 'empty'"
          :readonly="readonly"
          :disabled="disabled"
        >
          Design condition
        </v-btn>
      </v-btn-toggle>
    </v-list-item-title>
  </v-list-item>
  <v-list-item v-if="item.has_site_design_condition">
    <v-row no-gutters>
      <v-col>
        <CheckboxField
          :field="fields['has_load']"
          v-model="modelValue"
          :readonly="readonly"
          :disabled="disabled"
        />
      </v-col>
      <template v-if="item.has_load">
        <v-col cols="4">
          <TextField
            :field="fields['load']"
            v-model="modelValue"
            :readonly="readonly"
            :disabled="disabled"
          ></TextField>
        </v-col>
      </template>
    </v-row>
  </v-list-item>
  <v-list-item v-else>
    <v-card-subtitle class="pa-0 pl-0">
      No settings required. Choose 'design condition' above to set values.
    </v-card-subtitle>
  </v-list-item>
</template>

<style scoped>
.v-list-item-title {
  font-weight: bold;
}
</style>
